import React, {useEffect, useRef, useState, useTransition} from 'react';

import {useAppKitProvider} from "@reown/appkit/react";
import {useTranslation} from "react-i18next";
import {Alchemy} from "alchemy-sdk";
import {ethers} from "ethers";

import {useModalsContext} from "layouts";
import {
    SIZE,
    ERROR,
    SUCCESS,
    WARNING,
    INFORMATION,
    INTERACTION,
    STEPS_STATUS,
    TRANSFER_TYPE,
    BUTTON_VARIANT,
    WALLET_ERROR_CODE,
    isValidEthereumAddress
} from "utils";
import {globalService} from "store/Global/globalService";
import {useActionContext} from "../useActionContext";
import {CHAIN_TO_PARAMS_MAP} from "web3/chains";
import {KOMPETEGameAssetABI} from "web3/ABI";

import {ButtonV2, CheckBox2, Information, InputV2, Modal} from "components/atoms";
import {InfoItem, Process, Counter} from "./components"

const BazaarCurrentItemTransferNFTModal = () => {
    const [isEstimationPending, startEstimationStart] = useTransition()
    const [isPending, startTransition] = useTransition()
    const {walletProvider} = useAppKitProvider("eip155");
    const {currentModal, setCurrentModal} = useModalsContext()
    const {setActionDetails} = useActionContext()
    const {t} = useTranslation()

    const {tokenId, chainId, network} = currentModal.data

    const [tab, seTab] = useState(TRANSFER_TYPE.SINGLE)
    const [count, setCount] = useState(1)
    const [receiverAddress, setReceiverAddress] = useState("")
    const [estimatedFee, setEstimatedFee] = useState("")
    const [transferNFTs, setTransferNFTs] = useState([])
    const [informationStatus, setInformationStatus] = useState({
        type: INFORMATION.WARNING,
        text: WARNING.NFT_TRANSFER
    })

    useEffect(() => {
        if (receiverAddress && isValidEthereumAddress(receiverAddress)) {
            startEstimationStart(() => {
                startTransferTransaction(true,tab).then(setEstimatedFee)
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [receiverAddress, transferNFTs]);

    const transactionResultMessage = (status) => {
        if (status) {
            setInformationStatus({
                type: INFORMATION.SUCCESS,
                text: SUCCESS.TRANSFER_NFT
            })

            setActionDetails({
                type: INTERACTION.TRANSFER,
                isSuccess: true,
                data: {
                    token_id: tokenId,
                    receiver_address: receiverAddress,
                    count: count,
                },
            })

            setTimeout(() => {
                setCurrentModal({status: STEPS_STATUS.CLOSE})
            }, 3000)
        } else {
            setInformationStatus({
                type: INFORMATION.ERROR,
                text: ERROR.WRONG
            })
        }
    }

    const handleTransfer = () => {
        startTransition(async () => {
            if (tab === TRANSFER_TYPE.SINGLE) {
                await startTransferTransaction()
            } else {
                await startTransferTransaction()
            }
        })
    }

    const calculateEstimationToUSD = async (estimation, provider, ethPrice) => {
        const gasPrice = await provider.getGasPrice();
        const totalCostInWei = estimation.mul(gasPrice);
        const totalCostInEth = ethers.utils.formatEther(totalCostInWei);
        return parseFloat(totalCostInEth) * ethPrice;
    }

    const startTransferTransaction = async (isEstimate) => {
        const {amounts, ids} = transferNFTs.reduce((acc, nft) => {
            if (nft.isChecked) {
                return {
                    amounts: [...acc.amounts, nft.count],
                    ids: [...acc.ids, nft.tokenId]
                }
            }
            return acc
        }, {
            amounts: [],
            ids: []
        })

        try {
            if (!isValidEthereumAddress(receiverAddress) && !isEstimate) {
                setInformationStatus({
                    type: INFORMATION.ERROR,
                    text: ERROR.INVALID_ADDRESS
                })

                return
            }

            const chainParamsMap = CHAIN_TO_PARAMS_MAP[chainId]
            const provider = new ethers.providers.JsonRpcProvider(chainParamsMap.rpcURL)
            const collectionContract = new ethers.Contract(chainParamsMap.addresses.COLLECTION_ADDRESS, KOMPETEGameAssetABI, provider)

            const userProvider = new ethers.providers.Web3Provider(walletProvider)
            const signer = userProvider.getSigner()
            const senderAddress = await signer.getAddress()

            const contractCall = async (isOnlyEstimation) => {
                let estimation;

                if (tab === TRANSFER_TYPE.SINGLE) {
                    estimation = (await collectionContract
                        .connect(signer)
                        .estimateGas
                        .safeTransferFrom(senderAddress, receiverAddress, tokenId, Number(count), "0x"))
                        .mul(110)
                        .div(100)
                } else {
                    estimation = (await collectionContract
                        .connect(signer)
                        .estimateGas
                        .safeBatchTransferFrom(senderAddress, receiverAddress, ids, amounts, "0x"))
                        .mul(110)
                        .div(100)
                }

                if (isOnlyEstimation) {
                    return estimation
                }

                if (tab === TRANSFER_TYPE.SINGLE) {
                    return await collectionContract
                        .connect(signer)
                        .safeTransferFrom(senderAddress, receiverAddress, tokenId, Number(count), "0x", {
                            gasLimit: estimation,
                        })
                } else {
                    return await collectionContract
                        .connect(signer)
                        .safeBatchTransferFrom(senderAddress, receiverAddress, ids, amounts, "0x", {
                            gasLimit: estimation,
                        })
                }
            }

            if (isEstimate) {
                const estimation = await contractCall(true,tab)

                const ethPrice = await globalService.getEthPrice()
                const estimationToUSD = await calculateEstimationToUSD(estimation, provider, ethPrice)

                return `~ $${estimationToUSD.toFixed(5)}`
            }

            const tx = await contractCall(false,tab)

            const result = await tx.wait()

            transactionResultMessage(result.status)
        } catch (error) {
            if (isEstimate) {
                return 0
            } else {
                switch (error.code) {
                    case "ACTION_REJECTED":
                    case WALLET_ERROR_CODE.USER_REJECT_REQUEST:
                        setInformationStatus({type: INFORMATION.ERROR, text: ERROR.USER_DENIED_SIGNATURE})
                        break
                    case "CALL_EXCEPTION":
                        setInformationStatus({type: INFORMATION.ERROR, text: ERROR.TRANSACTION_FAILED})
                        break
                    default:
                        setInformationStatus({type: INFORMATION.ERROR, text: ERROR.WRONG})
                }
            }
        }
    }

    const initialDisabledButton = isPending || informationStatus.type === INFORMATION.SUCCESS
    const isDisabledButton = initialDisabledButton || !receiverAddress || (tab === "single" ? false : !transferNFTs.some(nft => nft.isChecked))

    return (
        <Modal
            isOpen={true}
            size={SIZE.SMALL}
            title={t("transferNftModalTitle")}
            onClose={() => !initialDisabledButton ? setCurrentModal({status: STEPS_STATUS.CLOSE}) : null}
        >
            <div className="bazaar-current-item_transfer-modal">
                <span className="bazaar_current-item_transfer-modal_under-title">{t("transferNftModalSubTitle")}</span>
                <div className="bazaar_current-item_transfer-modal_tabs">
                    <button
                        onClick={() => seTab(TRANSFER_TYPE.SINGLE)}
                        {...(tab === TRANSFER_TYPE.SINGLE && {className: "active"})}
                    >
                        {t("singleTransfer")}
                    </button>
                    <button
                        onClick={() => seTab(TRANSFER_TYPE.BATCH)}
                        {...(tab === TRANSFER_TYPE.BATCH && {className: "active"})}
                    >
                        {t("batchTransfer")}
                    </button>
                </div>
                <React.Fragment>
                    {{
                        [TRANSFER_TYPE.SINGLE]: <SingleTransfer
                            count={count}
                            fee={estimatedFee}
                            setCount={setCount}
                            isLoading={isEstimationPending}
                        />,
                        [TRANSFER_TYPE.BATCH]: <BatchTransfer
                            network={network}
                            chainId={chainId}
                            fee={estimatedFee}
                            isStartProcess={isPending}
                            transferNFTs={transferNFTs}
                            isLoading={isEstimationPending}
                            setTransferNFTs={setTransferNFTs}
                        />,
                    }[tab]}
                </React.Fragment>
                <div className="bazaar_current-item_transfer-modal_inps">
                    <InputV2
                        name="address"
                        size={SIZE.MEDIUM}
                        placeholder="0x..."
                        disabled={isPending}
                        value={receiverAddress}
                        label={t("recipientWalletAddress")}
                        onChange={(e) => setReceiverAddress(e.target.value)}
                    />
                </div>
                <div className="bazaar_current-item_transfer-modal_information">
                    {isPending
                        ? <Process/>
                        : <Information type={informationStatus.type} text={informationStatus.text}/>}
                </div>
                <div className="bazaar_current-item_transfer-modal_btns">
                    <ButtonV2
                        size={SIZE.MEDIUM}
                        disabled={initialDisabledButton}
                        onClick={() => setCurrentModal({status: STEPS_STATUS.CLOSE})}
                    >
                        cancel
                    </ButtonV2>
                    <ButtonV2
                        size={SIZE.MEDIUM}
                        onClick={handleTransfer}
                        variant={BUTTON_VARIANT.RED}
                        disabled={isDisabledButton}
                    >
                        confirm
                    </ButtonV2>
                </div>
            </div>
        </Modal>
    )
}

export default BazaarCurrentItemTransferNFTModal

const SingleTransfer = ({count, setCount, isLoading, fee}) => {
    const {currentModal} = useModalsContext()
    const {name, tokenId, owned} = currentModal.data

    return (
        <React.Fragment>
            <div className="bazaar_current-item_transfer-modal_item-info">
                <InfoItem name="item" value={name}/>
                <InfoItem name="tokenId" value={tokenId}/>
                <InfoItem isLoading={isLoading} name="fee" value={fee}/>
            </div>
            <div className="bazaar_current-item_transfer-modal_item-info">
                <InfoItem name="available" value={owned}/>
                <InfoItem name="quantity" value={<Counter max={owned} setCount={setCount} count={count}/>}/>
            </div>
        </React.Fragment>
    )
}

const BatchTransfer = ({chainId, setTransferNFTs, transferNFTs, fee, isLoading, isStartProcess, network}) => {
    const contentRef = useRef(null)
    const {t} = useTranslation()

    const {walletProvider} = useAppKitProvider("eip155");
    const [isPending, startTransition] = useTransition()
    const [pageKey, setPageKey] = useState(true)

    useEffect(() => {
        getNFTs()
        return () => setTransferNFTs([])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const contentElement = contentRef.current;
        if (contentElement) {
            contentElement.addEventListener("scroll", handleScroll);
        }
        return () => {
            if (contentElement) {
                contentElement.removeEventListener("scroll", handleScroll);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageKey, isPending, isStartProcess]);

    const handleScroll = () => {
        const contentElement = contentRef.current;
        if (contentElement) {
            const {scrollTop, scrollHeight, clientHeight} = contentElement;
            if (scrollTop + clientHeight >= scrollHeight - 1 && !isPending && pageKey && !isStartProcess) {
                getNFTs(pageKey)
            }
        }
    };

    const getNFTs = (pageKey = null) => {
        startTransition(async () => {
            const userProvider = new ethers.providers.Web3Provider(walletProvider)
            const address = await userProvider.getSigner().getAddress()

            const chainParamsMap = CHAIN_TO_PARAMS_MAP[chainId]
            const {rpcApiKey} = chainParamsMap

            const config = {
                apiKey: rpcApiKey,
                network: network,
            };

            const alchemy = new Alchemy(config);

            const result = await alchemy.nft.getNftsForOwner(address, {
                pageSize: 50,
                pageKey: pageKey,
            });

            setPageKey(result.pageKey)
            setTransferNFTs(prevState => [...prevState, ...result.ownedNfts.map(nft => ({
                ...nft,
                isChecked: false,
                count: 1
            }))])
        })
    }

    const selectedNFTsCount = transferNFTs.filter(nft => nft.isChecked).length

    return (
        <React.Fragment>
            <div className="bazaar_current-item_transfer-modal_nfts">
                <span className="bazaar_current-item_transfer-modal_nfts_title">{t("selectNfts")}</span>
                <div ref={contentRef} className="bazaar_current-item_transfer-modal_nfts_items">
                    {transferNFTs.map((nft) => {
                        return <NftItem
                            name={nft.name}
                            key={nft.tokenId}
                            tokenId={nft.tokenId}
                            balance={nft.balance}
                            isDisabled={isStartProcess}
                            isChecked={nft.isChecked ?? false}
                            setTransferNFTs={setTransferNFTs}
                        />
                    })}
                    {isPending &&
                        Array(5)
                            .fill("")
                            .map((_, index) => <div className="skeleton" key={index}></div>)}
                </div>
                <InfoItem
                    isLoading={isLoading}
                    value={fee ? `${t("fee")}: ${fee}` : ""}
                    name={`${t("selected")}: ${selectedNFTsCount} ${t("items")}`}
                />
            </div>
        </React.Fragment>
    )
}

const NftItem = ({balance, name, tokenId, isChecked, setTransferNFTs, isDisabled}) => {
    const [count, setCount] = useState(1)

    useEffect(() => {
        handleCount()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count])

    const handleCount = () => {
        setTransferNFTs(prevState => prevState.map(nft => nft.tokenId === tokenId ? {...nft, count} : nft))
    }

    const handleCheck = () => {
        setTransferNFTs(prevState => prevState.map(nft => nft.tokenId === tokenId ? {
            ...nft,
            isChecked: !nft.isChecked
        } : nft))
    }

    return (
        <div className="bazaar_current-item_transfer-modal_nft-item">
            <div className="bazaar_current-item_transfer-modal_nft-item_left">
                <CheckBox2
                    view="green"
                    checked={isChecked}
                    disable={isDisabled}
                    extent={SIZE.MEDIUM}
                    onChange={handleCheck}
                >
                    {name?.length > 15 ? `${name.slice(0, 15)}...` : name}
                </CheckBox2>
            </div>
            <div className="bazaar_current-item_transfer-modal_nft-item_right">
                <span>ID: {tokenId}</span>
                <Counter isDisabled={isDisabled} count={count} setCount={setCount} max={balance}/>
            </div>
        </div>
    )
}